import { PADRAO3_BROWSER } from './voiceProfiles';

const FOREIGN = /en-in|hi-in|en_in|hi_in|india/i;
const LIST_MARKER = /^(?:[-*•–—]|\d{1,3}[.)])\s+/;
const HEADING = /^#{1,6}\s+/;

export interface VoiceLike {
  lang: string;
  name: string;
}

export interface SpeechSegment {
  text: string;
  pauseAfterMs: number;
  /** Seção da análise já na tela (toggle ou bloco). */
  section?: string;
  /** Título de lista: áudio próprio, com pausa antes do primeiro item. */
  listHeader?: boolean;
  /** Item de lista: áudio próprio, com pausa antes do próximo item. */
  listItem?: boolean;
}

/** Pausas e tamanho de trecho (iguais nos três padrões). A voz do navegador usa estes nomes. */
export const SPEECH_CHUNK_CHARS = PADRAO3_BROWSER.chunkChars;
export const PAUSE_COMMA_MS = PADRAO3_BROWSER.pauses.commaMs;
export const PAUSE_SEMICOLON_MS = PADRAO3_BROWSER.pauses.semicolonMs;
export const PAUSE_PERIOD_MS = PADRAO3_BROWSER.pauses.periodMs;
export const PAUSE_INLINE_QUESTION_MS = PADRAO3_BROWSER.pauses.inlineQuestionMs;
export const PAUSE_BULLET_MS = PADRAO3_BROWSER.pauses.bulletMs;
export const PAUSE_SECTION_MS = PADRAO3_BROWSER.pauses.sectionMs;
export const PAUSE_QUESTION_MS = PADRAO3_BROWSER.pauses.questionMs;

const ABBREV = /^(?:art|arts|n|no|nº|dr|dra|sr|sra|srs)$/i;

function pauses() {
  return PADRAO3_BROWSER.pauses;
}

function chunkLimit() {
  return PADRAO3_BROWSER.chunkChars;
}

export function pickPortugueseVoice<T extends VoiceLike>(voices: T[]): T | null {
  const profile = PADRAO3_BROWSER;
  const pt = voices.filter(
    (voice) =>
      /pt[-_]?br/i.test(voice.lang) &&
      !FOREIGN.test(voice.lang) &&
      !FOREIGN.test(voice.name),
  );
  for (const preferred of profile.preferredVoiceNames) {
    const needle = preferred.toLowerCase();
    const match = pt.find((voice) => voice.name.toLowerCase().includes(needle));
    if (match) return match;
  }
  // Fallback histórico: Luciana / Francisca / Google português
  return (
    pt.find((voice) => /luciana|francisca|google portugu[eê]s/i.test(voice.name)) ||
    pt[0] ||
    null
  );
}

function pauseForEnding(text: string): number {
  const value = text.trim();
  const p = pauses();
  if (value.endsWith('?')) return p.inlineQuestionMs;
  if (value.endsWith(';')) return p.semicolonMs;
  if (value.endsWith(',')) return p.commaMs;
  if (value.endsWith('.') || value.endsWith('!')) return p.periodMs;
  return p.commaMs;
}

function breakRank(text: string, index: number): number {
  if (index < 0 || index >= text.length - 1 || !/\s/.test(text[index + 1])) return 0;
  const mark = text[index];
  if (mark === '.' || mark === '!' || mark === '?') {
    if (mark === '.') {
      const word = text.slice(0, index).match(/(\S+)$/)?.[1] ?? '';
      if (ABBREV.test(word)) return 0;
    }
    return 3;
  }
  if (mark === ';') return 2;
  if (mark === ',') return 1;
  return 0;
}

/** Corte mais à direita, na ordem ponto/interrogação, ponto e vírgula, vírgula. */
function bestCut(text: string, maxChars: number): number {
  let bestRank = 0;
  let bestEnd = -1;
  const limit = Math.min(text.length, maxChars);
  for (let index = 0; index < limit; index += 1) {
    const rank = breakRank(text, index);
    if (rank > 0 && rank >= bestRank) {
      bestRank = rank;
      bestEnd = index + 1;
    }
  }
  return bestEnd;
}

function splitSpeech(text: string, maxChars: number): SpeechSegment[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.length <= maxChars) {
    return [{ text: trimmed, pauseAfterMs: pauseForEnding(trimmed) }];
  }
  const end = bestCut(trimmed, maxChars);
  let left: string;
  let right: string;
  let pause: number;
  if (end > 0) {
    left = trimmed.slice(0, end).trim();
    right = trimmed.slice(end).trim();
    pause = pauseForEnding(left);
  } else {
    let wordAt = trimmed.lastIndexOf(' ', maxChars);
    if (wordAt <= 0) wordAt = maxChars;
    left = trimmed.slice(0, wordAt).trim();
    right = trimmed.slice(wordAt).trim();
    pause = pauses().commaMs;
    if (!left) {
      left = trimmed.slice(0, maxChars);
      right = trimmed.slice(maxChars).trim();
    }
  }
  if (!right) return [{ text: left, pauseAfterMs: pauseForEnding(left) }];
  return [{ text: left, pauseAfterMs: pause }, ...splitSpeech(right, maxChars)];
}

export function speechPieces(text: string, maxChars = chunkLimit()): SpeechSegment[] {
  return splitSpeech(text, maxChars);
}

export function speechChunks(text: string, maxChars = chunkLimit()): string[] {
  return speechPieces(text, maxChars).map((piece) => piece.text);
}

/** Separa frases e pontos e vírgulas sem juntar de novo. A vírgula permanece na frase. */
export function speechBreaths(text: string): string[] {
  const parts: string[] = [];
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (breakRank(text, index) >= 2) {
      const piece = text.slice(start, index + 1).trim();
      if (piece) parts.push(piece);
      start = index + 1;
    }
  }
  const rest = text.slice(start).trim();
  if (rest) parts.push(rest);
  return parts;
}

/** Tira ### e o asterisco de lista. O número ou a letra do título permanecem. */
function stripLineMarks(line: string): string {
  const lead = line.match(/^\s*/)?.[0] ?? '';
  const body = line.slice(lead.length);
  const withoutHash = body.replace(/(^|\s)#{1,6}\s*/g, '$1');
  const stripped = withoutHash !== body
    ? withoutHash.replace(/^(?:[-*•–—])\s+/, '')
    : body.replace(LIST_MARKER, '');
  return `${lead}${stripped}`;
}

/** Texto da conversa, com as quebras. A cerquilha não aparece. O gravado não muda. */
export function visibleChatText(text: string): string {
  return text.replace(/\r\n/g, '\n').split('\n').map((line) => stripLineMarks(line)).join('\n');
}

/** Tira markdown e o marcador da lista. O conteúdo fica; o símbolo não é falado. */
export function cleanSpeechText(text: string): string {
  return visibleChatText(text)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|\s)[*_]([^*_\n]+)[*_](?=\s|$)/g, '$1$2')
    .replace(/`+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const YEAR_UNITS = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const YEAR_TEENS = ['dez', 'onze', 'doze', 'treze', 'catorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const YEAR_TENS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];

/** Ano falado em pt-BR. 2026 vira “dois mil e vinte e seis”. */
export function yearInWords(year: number): string {
  if (year < 1900 || year > 2099) return String(year);
  const century = year >= 2000 ? 'dois mil' : 'mil novecentos';
  const rest = year % 100;
  if (rest === 0) return century;
  if (rest < 10) return `${century} e ${YEAR_UNITS[rest]}`;
  if (rest < 20) return `${century} e ${YEAR_TEENS[rest - 10]}`;
  const ten = Math.floor(rest / 10);
  const unit = rest % 10;
  return unit ? `${century} e ${YEAR_TENS[ten]} e ${YEAR_UNITS[unit]}` : `${century} e ${YEAR_TENS[ten]}`;
}

const NORM_YEAR = new RegExp(
  '\\b(decreto-lei|decreto\\s+lei|instru[cç][aã]o\\s+normativa|medida\\s+provis[oó]ria|decis[aã]o|decreto|portaria|resolu[cç][aã]o|norma|lei)' +
    '(\\s+(?:n[ºo°.]\\s*)?)' +
    '(\\d{1,3}(?:\\.\\d{3})+|\\d{1,6})/((?:19|20)\\d{2})\\b',
  'gi',
);

/**
 * Na fala, “Portaria 1.701/2026” vira “Portaria 1.701 de dois mil e vinte e seis”.
 * Data com dia, mês e ano permanece.
 */
export function speakableNormYear(text: string): string {
  NORM_YEAR.lastIndex = 0;
  const dates: string[] = [];
  const shielded = text.replace(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/g, (date) => {
    dates.push(date);
    return `\u0000${dates.length - 1}\u0000`;
  });
  const typed = shielded.replace(NORM_YEAR, (_match, type, gap, num, year) => {
    return `${type}${gap}${num} de ${yearInWords(Number(year))}`;
  });
  const spoken = typed.replace(
    /\b(n\.?\s*[ºo°.]\s*)(\d{1,3}(?:\.\d{3})+|\d{1,6})\/((?:19|20)\d{2})\b/gi,
    (_match, prefix, num, year) => `${prefix}${num} de ${yearInWords(Number(year))}`,
  );
  return spoken.replace(/\u0000(\d+)\u0000/g, (_match, index) => dates[Number(index)]);
}

function pushPieces(out: SpeechSegment[], text: string, pauseAfterMs: number | null) {
  const pieces = speechPieces(text).filter((piece) => piece.text);
  pieces.forEach((piece, index) => {
    const isLast = index === pieces.length - 1;
    out.push({
      text: piece.text,
      pauseAfterMs: isLast && pauseAfterMs != null ? pauseAfterMs : piece.pauseAfterMs,
    });
  });
}

/**
 * Texto livre: frases com pausa curta; linha de lista vira o conteúdo do item,
 * com pausa de item no fim. O último bloco usa trailingPauseMs.
 */
export function parseSpeechText(text: string, trailingPauseMs = pauses().periodMs): SpeechSegment[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: Array<{ kind: 'prose' | 'bullet'; text: string }> = [];
  let prose: string[] = [];
  const flushProse = () => {
    const joined = cleanSpeechText(prose.join(' '));
    prose = [];
    if (joined) blocks.push({ kind: 'prose', text: joined });
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushProse();
      continue;
    }
    if (LIST_MARKER.test(line) || HEADING.test(line)) {
      flushProse();
      const content = cleanSpeechText(line);
      if (content) blocks.push({ kind: 'bullet', text: content });
      continue;
    }
    prose.push(line);
  }
  flushProse();

  const segments: SpeechSegment[] = [];
  const bulletPause = pauses().bulletMs;
  blocks.forEach((block, index) => {
    const isLast = index === blocks.length - 1;
    const endPause = isLast ? trailingPauseMs : block.kind === 'bullet' ? bulletPause : null;
    const from = segments.length;
    pushPieces(segments, block.text, endPause);
    if (block.kind === 'bullet') {
      for (let piece = from; piece < segments.length; piece += 1) segments[piece].listItem = true;
    }
  });
  return segments;
}

export function expandSegments(segments: SpeechSegment[]): SpeechSegment[] {
  const out: SpeechSegment[] = [];
  for (const segment of segments) {
    pushPieces(out, cleanSpeechText(segment.text), segment.pauseAfterMs);
  }
  return out;
}

/** Limite de um POST TTS (Speechify e Fish). Cortes menores são só do Chrome. */
export const API_SPEECH_MAX_CHARS = 2000;

const API_ABBREV = /^(?:art|arts|n|no|nº|dr|dra|sr|sra|srs|etc|obs)$/i;

/**
 * Texto falado da API: pontuação vira espaço para a voz não respirar em cada sinal.
 * Mantém número (1.2, 1,5, 10:30) e abreviação curta (art., n.).
 */
export function softenApiProsody(text: string): string {
  let out = '';
  for (let index = 0; index < text.length; index += 1) {
    const ch = text[index];
    if (!'.,:;!?'.includes(ch)) {
      out += ch;
      continue;
    }
    const prev = text[index - 1] || '';
    const next = text[index + 1] || '';
    if ((ch === '.' || ch === ',' || ch === ':') && /\d/.test(prev) && /\d/.test(next)) {
      out += ch;
      continue;
    }
    if (ch === '.') {
      const word = out.match(/(\S+)$/)?.[1] ?? '';
      if (API_ABBREV.test(word)) {
        out += ch;
        continue;
      }
    }
    out += ' ';
  }
  return out.replace(/\s+/g, ' ').trim();
}

/**
 * Texto contínuo para Greening1/2. Sem pausa artificial.
 * Só parte se passar do limite da API, no espaço.
 */
/** Frases da resposta falada. Ponto de abreviação e de item numerado não cortam. */
export function speechSentences(text: string): string[] {
  const cleaned = cleanSpeechText(text);
  if (!cleaned) return [];
  const abbrev = /^(?:art|arts|n|no|nº)$/i;
  const parts: string[] = [];
  let start = 0;
  for (let index = 0; index < cleaned.length; index += 1) {
    const ch = cleaned[index];
    if (ch !== '.' && ch !== '!' && ch !== '?') continue;
    let spaces = index + 1;
    while (spaces < cleaned.length && /\s/.test(cleaned[spaces])) spaces += 1;
    if (spaces === index + 1) continue;
    if (ch === '.') {
      const word = cleaned.slice(start, index).match(/(\S+)$/)?.[1] ?? '';
      if (abbrev.test(word) || /^\d{1,3}$/.test(word)) continue;
    }
    parts.push(cleaned.slice(start, index + 1).trim());
    start = spaces;
    index = spaces - 1;
  }
  const tail = cleaned.slice(start).trim();
  if (tail) parts.push(tail);
  return parts.length ? parts : [cleaned];
}

export function apiSpeechSegments(text: string, maxChars = API_SPEECH_MAX_CHARS): SpeechSegment[] {
  const cleaned = softenApiProsody(cleanSpeechText(text));
  if (!cleaned) return [];
  return splitApiSpeech(cleaned, maxChars);
}

/** Pausa entre o rótulo com ? e a resposta, só na fila da API. */
export const API_QUESTION_PAUSE_MS = 1000;

/** Pausa entre o título da lista e o primeiro item. */
export const LIST_HEADER_PAUSE_MS = 1000;

/** Pausa mínima no dois-pontos de rótulo. Hora e razão não cortam. */
export const COLON_PAUSE_MS = 250;

/** Pausa depois do ponto, nas duas cápsulas. */
export const PERIOD_GAP_MS = 1000;

/** Depois do ponto, 1 s. Pausa já marcada (item, título, dois-pontos) permanece, no máximo 1 s. */
export function gapAfterRun(pauseAfterMs: number, hasNext: boolean): number {
  if (!hasNext) return 0;
  if (pauseAfterMs > 0) return Math.min(pauseAfterMs, PERIOD_GAP_MS);
  return PERIOD_GAP_MS;
}

/** A prosa junta no máximo isto, nas duas cápsulas. */
export const PROSE_RUN_MAX_CHARS = 420;
export const PROSE_RUN_MAX_SENTENCES = 2;

/** A frase seguinte ainda cabe no mesmo áudio. A primeira sempre cabe. */
export function proseRunAccepts(sentenceCount: number, joinedLength: number, nextLength: number): boolean {
  if (sentenceCount >= PROSE_RUN_MAX_SENTENCES) return false;
  if (sentenceCount > 0 && joinedLength + 1 + nextLength > PROSE_RUN_MAX_CHARS) return false;
  return true;
}

/** Partes em volta de um dois-pontos que não está entre dígitos. O sinal sai. */
export function splitLabelColons(text: string): string[] {
  const parts: string[] = [];
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== ':') continue;
    const prev = text[index - 1] || '';
    const next = text[index + 1] || '';
    if (/\d/.test(prev) && /\d/.test(next)) continue;
    const left = text.slice(start, index).trim();
    if (left) parts.push(left);
    start = index + 1;
  }
  const tail = text.slice(start).trim();
  if (tail) parts.push(tail);
  return parts;
}

/**
 * Um clipe por lado do dois-pontos. O lado esquerdo espera 250 ms.
 * O último lado fica com a pausa do item, do título ou da frase.
 */
export function clauseSegments(
  text: string,
  trailingPause: number,
  extra: Partial<SpeechSegment> = {},
  options?: { soften?: boolean; maxChars?: number },
): SpeechSegment[] {
  const soften = options?.soften !== false;
  const maxChars = options?.maxChars ?? API_SPEECH_MAX_CHARS;
  const clauses = splitLabelColons(text);
  const source = clauses.length ? clauses : [text.trim()].filter(Boolean);
  const out: SpeechSegment[] = [];
  source.forEach((clause, clauseIndex) => {
    const raw = soften
      ? apiSpeechSegments(clause, maxChars)
      : [{ text: clause.trim(), pauseAfterMs: 0 }];
    const pieces = raw.filter((piece) => piece.text);
    pieces.forEach((piece, pieceIndex) => {
      const lastClause = clauseIndex === source.length - 1;
      const lastPiece = pieceIndex === pieces.length - 1;
      let pauseAfterMs = 0;
      if (lastClause && lastPiece) pauseAfterMs = trailingPause;
      else if (!lastClause && lastPiece) pauseAfterMs = COLON_PAUSE_MS;
      const segment: SpeechSegment = { text: piece.text, pauseAfterMs, section: extra.section };
      if (extra.listHeader) segment.listHeader = true;
      if (extra.listItem) segment.listItem = true;
      out.push(segment);
    });
  });
  return out;
}

function isQuestionLabel(text: string): boolean {
  return /\?\s*$/.test(text.trim());
}

/**
 * Fila da API: uma frase por clipe, com a pontuação suavizada.
 * Título, item e rótulo de pergunta continuam clipe próprio.
 */
export function apiQueueFromSegments(segments: SpeechSegment[], maxChars = API_SPEECH_MAX_CHARS): SpeechSegment[] {
  const out: SpeechSegment[] = [];
  let bucket: SpeechSegment[] = [];
  const flush = () => {
    if (!bucket.length) return;
    const section = bucket[0].section;
    const joined = cleanSpeechText(bucket.map((segment) => segment.text).join(' '));
    const sentences = speechSentences(joined);
    const lines = sentences.length ? sentences : joined ? [joined] : [];
    lines.forEach((sentence) => {
      clauseSegments(sentence, 0, { section }, { maxChars }).forEach((piece) => out.push(piece));
    });
    bucket = [];
  };
  for (const segment of segments) {
    if (isQuestionLabel(segment.text)) {
      flush();
      clauseSegments(segment.text.trim(), API_QUESTION_PAUSE_MS, { section: segment.section }, { soften: false })
        .forEach((piece) => out.push(piece));
      continue;
    }
    if (segment.listHeader) {
      flush();
      clauseSegments(segment.text.trim(), LIST_HEADER_PAUSE_MS, {
        section: segment.section,
        listHeader: true,
      }).forEach((piece) => out.push(piece));
      continue;
    }
    if (segment.listItem) {
      flush();
      clauseSegments(segment.text.trim(), segment.pauseAfterMs, {
        section: segment.section,
        listItem: true,
      }).forEach((piece) => out.push(piece));
      continue;
    }
    if (bucket.length && bucket[0].section !== segment.section) flush();
    bucket.push(segment);
  }
  flush();
  return out;
}

function splitApiSpeech(text: string, maxChars: number): SpeechSegment[] {
  if (text.length <= maxChars) return [{ text, pauseAfterMs: 0 }];
  let wordAt = text.lastIndexOf(' ', maxChars);
  if (wordAt <= 0) wordAt = maxChars;
  const left = text.slice(0, wordAt).trim();
  const right = text.slice(wordAt).trim();
  if (!left) return [{ text: text.slice(0, maxChars), pauseAfterMs: 0 }];
  if (!right) return [{ text: left, pauseAfterMs: 0 }];
  return [{ text: left, pauseAfterMs: 0 }, ...splitApiSpeech(right, maxChars)];
}
